import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEventStats, getTicketTypes } from '../services/api';
import Navbar from '../components/Navbar';
import { useTheme } from '../context/ThemeContext';
import useWindowSize from '../hooks/useWindowSize';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import EuroIcon from '@mui/icons-material/Euro';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

function Promotor() {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [typesCache, setTypesCache] = useState({});
  const [typesLoading, setTypesLoading] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!user || (user.role !== 'promotor' && user.role !== 'admin')) {
      navigate('/login');
      return;
    }
    getEventStats()
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExpand = async (eventId) => {
    if (expandedId === eventId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(eventId);
    if (!typesCache[eventId]) {
      setTypesLoading(eventId);
      try {
        const res = await getTicketTypes(eventId);
        setTypesCache(prev => ({ ...prev, [eventId]: res.data.ticket_types || [] }));
      } catch {
        setTypesCache(prev => ({ ...prev, [eventId]: [] }));
      }
      setTypesLoading(null);
    }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Navbar />
      <p style={{ color: colors.textSecondary, padding: '2rem' }}>Cargando reportes...</p>
    </div>
  );

  const { events = [], totals = {} } = data || {};

  const renderTypesBreakdown = (eventId) => {
    const types = typesCache[eventId];
    if (typesLoading === eventId) {
      return <p style={{ color: colors.textSecondary, fontSize: '0.82rem', padding: '0.8rem 0' }}>Cargando tipos de entrada...</p>;
    }
    if (!types || types.length === 0) {
      return <p style={{ color: colors.textSecondary, fontSize: '0.82rem', padding: '0.8rem 0' }}>Este evento no tiene tipos de entrada configurados (precio único).</p>;
    }
    const sortedTypes = [...types].sort((a, b) => (b.is_free ? 1 : 0) - (a.is_free ? 1 : 0));
    return (
      <div style={{ padding: '0.8rem 0' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {sortedTypes.map(tt => {
            const revenue = (parseFloat(tt.price) * tt.sold).toFixed(2);
            return (
              <div key={tt.id} style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr',
                gap: '0.4rem',
                backgroundColor: colors.bgTertiary,
                borderRadius: '8px',
                padding: '0.6rem 0.9rem',
                alignItems: 'center'
              }}>
                <span style={{ color: colors.text, fontWeight: '500', fontSize: '0.85rem' }}>{tt.name}</span>
                <span style={{ color: colors.accent, fontSize: '0.82rem' }}>
                  <span style={{ color: colors.textSecondary, fontSize: '0.72rem' }}>Vendidas: </span>{tt.sold} / {tt.capacity}
                </span>
                <span style={{ color: colors.text, fontSize: '0.82rem' }}>
                  <span style={{ color: colors.textSecondary, fontSize: '0.72rem' }}>Precio: </span>{parseFloat(tt.price).toFixed(2)}€
                </span>
                <span style={{ color: '#00cc66', fontWeight: 'bold', fontSize: '0.82rem' }}>
                  <span style={{ color: colors.textSecondary, fontSize: '0.72rem', fontWeight: 'normal' }}>Ingresos: </span>{revenue}€
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Navbar />
      <div style={{ padding: isMobile ? '1rem' : '2rem', maxWidth: '1200px', margin: '0 auto' }}>

        <h1 style={{ color: colors.text, fontSize: isMobile ? '1.4rem' : '2rem', fontWeight: 'bold', marginBottom: '0.3rem' }}>
          {isAdmin ? 'Reportes globales' : 'Mis reportes'}
        </h1>
        <p style={{ color: colors.textSecondary, marginBottom: '2rem', fontSize: '0.9rem' }}>
          {isAdmin ? 'Estadisticas de todos los eventos' : 'Estadisticas de tus eventos'}
        </p>

        {/* Tarjetas resumen */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(2, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
          {[
            { label: 'Entradas vendidas', value: `${totals.tickets_sold || 0}`, icon: <ConfirmationNumberIcon style={{ fontSize: 22, color: colors.accent }} />, color: colors.accent },
            { label: 'Ingresos eventos', value: `${parseFloat(totals.revenue || 0).toFixed(2)}€`, icon: <EuroIcon style={{ fontSize: 22, color: '#00cc66' }} />, color: '#00cc66' },
          ].map((card, i) => (
            <div key={i} style={{ backgroundColor: colors.bgSecondary, borderRadius: '12px', border: `1px solid ${colors.border}`, padding: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                {card.icon}
                <span style={{ color: colors.textSecondary, fontSize: '0.78rem' }}>{card.label}</span>
              </div>
              <p style={{ color: card.color, fontSize: isMobile ? '1.1rem' : '1.5rem', fontWeight: 'bold', margin: 0 }}>{card.value}</p>
            </div>
          ))}
        </div>

        {events.length === 0 ? (
          <div style={{ backgroundColor: colors.bgSecondary, borderRadius: '12px', border: `1px solid ${colors.border}`, padding: '2rem', textAlign: 'center' }}>
            <p style={{ color: colors.textSecondary }}>
              {isAdmin ? 'No hay eventos.' : 'No tienes eventos asignados. Contacta con el administrador.'}
            </p>
          </div>
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
            {events.map(event => (
              <div key={event.id} style={{ backgroundColor: colors.bgSecondary, borderRadius: '12px', border: `1px solid ${colors.border}`, padding: '1rem' }}>
                <div
                  style={{ marginBottom: '0.8rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
                  onClick={() => toggleExpand(event.id)}
                >
                  <div>
                    <p style={{ color: colors.text, fontWeight: 'bold', fontSize: '0.95rem', margin: 0 }}>{event.name}</p>
                    <p style={{ color: colors.textSecondary, fontSize: '0.78rem', margin: '0.2rem 0 0' }}>
                      {new Date(event.date).toLocaleDateString('es-ES')} · {event.location}
                    </p>
                  </div>
                  {expandedId === event.id ? <ExpandLessIcon style={{ color: colors.accent }} /> : <ExpandMoreIcon style={{ color: colors.textSecondary }} />}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  <div>
                    <p style={{ color: colors.textSecondary, fontSize: '0.72rem', margin: 0 }}>Vendidas</p>
                    <p style={{ color: colors.accent, fontWeight: 'bold', margin: 0 }}>{event.tickets_sold} / {event.total_tickets}</p>
                  </div>
                  <div>
                    <p style={{ color: colors.textSecondary, fontSize: '0.72rem', margin: 0 }}>Precio</p>
                    <p style={{ color: colors.text, margin: 0 }}>{event.price}€</p>
                  </div>
                  <div>
                    <p style={{ color: colors.textSecondary, fontSize: '0.72rem', margin: 0 }}>Ingresos evento</p>
                    <p style={{ color: '#00cc66', fontWeight: 'bold', margin: 0 }}>{event.revenue}€</p>
                  </div>
                </div>
                {isAdmin && (
                  <p style={{ color: colors.textSecondary, fontSize: '0.78rem', marginTop: '0.6rem', paddingTop: '0.6rem', borderTop: `1px solid ${colors.border}` }}>
                    Promotor: <span style={{ color: colors.text }}>{event.promotor_name || 'Sin asignar'}</span>
                  </p>
                )}
                {expandedId === event.id && (
                  <div style={{ borderTop: `1px solid ${colors.border}`, marginTop: '0.6rem' }}>
                    {renderTypesBreakdown(event.id)}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ backgroundColor: colors.bgSecondary, borderRadius: '12px', border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: colors.bgTertiary }}>
                    {['', 'Evento', 'Fecha', 'Vendidas', 'Precio', 'Ingresos evento', ...(isAdmin ? ['Promotor'] : [])].map((col, i) => (
                      <th key={i} style={{ color: colors.accent, fontSize: '0.82rem', fontWeight: 'bold', padding: '0.9rem 1rem', textAlign: 'left', whiteSpace: 'nowrap' }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <React.Fragment key={event.id}>
                      <tr
                        style={{ borderTop: `1px solid ${colors.border}`, cursor: 'pointer' }}
                        onClick={() => toggleExpand(event.id)}
                      >
                        <td style={{ padding: '0.9rem 0.5rem 0.9rem 1rem', width: '32px' }}>
                          {expandedId === event.id ? <ExpandLessIcon style={{ color: colors.accent, fontSize: 20 }} /> : <ExpandMoreIcon style={{ color: colors.textSecondary, fontSize: 20 }} />}
                        </td>
                        <td style={{ padding: '0.9rem 1rem' }}>
                          <p style={{ color: colors.text, fontWeight: 'bold', fontSize: '0.9rem', margin: 0 }}>{event.name}</p>
                          <p style={{ color: colors.textSecondary, fontSize: '0.78rem', margin: 0 }}>{event.location}</p>
                        </td>
                        <td style={{ color: colors.textSecondary, fontSize: '0.85rem', padding: '0.9rem 1rem', whiteSpace: 'nowrap' }}>
                          {new Date(event.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                        <td style={{ padding: '0.9rem 1rem' }}>
                          <span style={{ color: colors.accent, fontWeight: 'bold' }}>{event.tickets_sold}</span>
                          <span style={{ color: colors.textSecondary, fontSize: '0.8rem' }}> / {event.total_tickets}</span>
                        </td>
                        <td style={{ color: colors.text, padding: '0.9rem 1rem' }}>{event.price}€</td>
                        <td style={{ color: '#00cc66', fontWeight: 'bold', padding: '0.9rem 1rem' }}>{event.revenue}€</td>
                        {isAdmin && (
                          <td style={{ color: colors.textSecondary, fontSize: '0.82rem', padding: '0.9rem 1rem' }}>
                            {event.promotor_name || <span style={{ opacity: 0.4 }}>Sin asignar</span>}
                          </td>
                        )}
                      </tr>
                      {expandedId === event.id && (
                        <tr style={{ backgroundColor: colors.bg }}>
                          <td></td>
                          <td colSpan={isAdmin ? 5 : 4} style={{ padding: '0 1rem 0.8rem' }}>
                            {renderTypesBreakdown(event.id)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: `2px solid ${colors.border}`, backgroundColor: colors.bgTertiary }}>
                    <td></td>
                    <td colSpan={2} style={{ color: colors.text, fontWeight: 'bold', padding: '0.9rem 1rem' }}>TOTALES</td>
                    <td style={{ color: colors.accent, fontWeight: 'bold', padding: '0.9rem 1rem' }}>{totals.tickets_sold}</td>
                    <td style={{ padding: '0.9rem 1rem' }}></td>
                    <td style={{ color: '#00cc66', fontWeight: 'bold', padding: '0.9rem 1rem' }}>{parseFloat(totals.revenue || 0).toFixed(2)}€</td>
                    {isAdmin && <td></td>}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Promotor;